import React from "react";
import { cn, type ClassValue } from "../lib/cn";

export type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Visual density.
   * - `default`: matches login card spacing
   * - `compact`: tighter for tables/lists
   */
  variant?: "default" | "compact";
  /**
   * If true, uses the stronger surface background (`--kkp-surface-2`).
   */
  elevated?: boolean;
  className?: string;
};

/**
 * Minimal on-brand "Card" primitive.
 *
 * Design goals:
 * - Match the login page glassy surface + border
 * - Use brand CSS variables (no random hardcoded colors)
 * - Stay framework-light and composable
 */
export function Card({
  variant = "default",
  elevated = false,
  className,
  ...props
}: CardProps) {
  const base: ClassValue = [
    "rounded-[var(--kkp-radius)]",
    "border border-[color:var(--kkp-border)]",
    elevated
      ? "bg-[color:var(--kkp-surface-2)]"
      : "bg-[color:var(--kkp-surface)]",
    "shadow-[0_20px_40px_rgba(0,0,0,0.18),_inset_0_1px_0_rgba(255,255,255,0.06)]",
  ];

  const padding: Record<NonNullable<CardProps["variant"]>, ClassValue> = {
    default: "p-4",
    compact: "p-3",
  };

  return <div className={cn(base, padding[variant], className)} {...props} />;
}

export type CardHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  className?: string;
};

export function CardHeader({ className, ...props }: CardHeaderProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1",
        "pb-3 mb-3 border-b border-[color:var(--kkp-border)]",
        className,
      )}
      {...props}
    />
  );
}

export type CardTitleProps = React.HTMLAttributes<HTMLHeadingElement> & {
  as?: "h1" | "h2" | "h3" | "h4";
  className?: string;
};

export function CardTitle({ as = "h3", className, ...props }: CardTitleProps) {
  const Comp = as;
  return (
    <Comp
      className={cn(
        "text-sm font-bold text-[color:rgba(255,255,255,0.92)]",
        className,
      )}
      {...props}
    />
  );
}

export type CardDescriptionProps =
  React.HTMLAttributes<HTMLParagraphElement> & {
    className?: string;
  };

export function CardDescription({ className, ...props }: CardDescriptionProps) {
  return (
    <p
      className={cn(
        "text-xs text-[color:var(--kkp-text-muted)] leading-snug",
        className,
      )}
      {...props}
    />
  );
}

export type CardBodyProps = React.HTMLAttributes<HTMLDivElement> & {
  className?: string;
};

export function CardBody({ className, ...props }: CardBodyProps) {
  return <div className={cn("grid gap-3", className)} {...props} />;
}

export type CardFooterProps = React.HTMLAttributes<HTMLDivElement> & {
  className?: string;
};

export function CardFooter({ className, ...props }: CardFooterProps) {
  return (
    <div
      className={cn(
        "pt-3 mt-3 border-t border-[color:var(--kkp-border)]",
        "flex items-center justify-end gap-2",
        className,
      )}
      {...props}
    />
  );
}
